import { FC } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Rule } from 'entities/campaign/rules';
import { isMandatory } from 'entities/campaign/rulesets/mandatory-and-optional-rules';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Actions as RulesetActions, Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import { State } from 'store';
import { isEqual } from '../../../../../core/ui/utils/isEqual';
import { Configs } from '../configs';
import StepControls from './StepControls';
import Block from './Block';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';

interface IOwnProps {
  rulesetId: string;
  ruleIndex: number;
  rule: Rule;
  rules: Rule[];
  campaignState: CampaignState;
}

interface IStateProps extends IOwnProps {
  savedRule: Rule;
  isMandatory: boolean;
}

interface IDispatchProps {
  editRule: (rulesetId: string, ruleIndex: number, changes: Rule) => void;
  deleteRule: (rulesetId: string, ruleIndex: number, rule: Rule) => void;
}

export type IProps = IStateProps & IDispatchProps;

export const Step: FC<IProps> = props => {
  const { t } = useTranslation('campaigns');
  const config = Configs.getRuleConfig(t, props.rule.type);
  const Form = config && config.form;

  const removeRule = props.isMandatory
    ? undefined
    : () => props.deleteRule(props.rulesetId, props.ruleIndex, props.rule);

  const hasChanges = !isEqual(props.rule, props.savedRule);
  const revertRule =
    props.savedRule && hasChanges ? () => props.editRule(props.rulesetId, props.ruleIndex, props.savedRule) : undefined;

  return config ? (
    <Block
      icon={config.glyph}
      title={config.title + (hasChanges ? '*' : '')}
      value={config.getValue(props.rule) || '-'}
      valid={config.valid(props.rule, props.rules)}
      menu={(revertRule || removeRule) && <StepControls revert={revertRule} discard={removeRule} />}
      form={<Form rulesetId={props.rulesetId} ruleIndex={props.ruleIndex} rule={props.rule} />}
      campaignState={props.campaignState}
      disableOpen={false}
    />
  ) : null;
};

export const mapState = (state: State, ownProps: IOwnProps): IStateProps => {
  const savedRuleset = RulesetSelectors.getEntity(state, ownProps.rulesetId);
  const updatedOrNewRuleset = RulesetSelectors.getUpdatedEntity(state, ownProps.rulesetId);
  const campaign = CampaignSelectors.getEntity(state, (savedRuleset || updatedOrNewRuleset).campaignId);
  const savedRule = savedRuleset && savedRuleset.rules && savedRuleset.rules[ownProps.ruleIndex];
  const updatedOrNewRule =
    updatedOrNewRuleset && updatedOrNewRuleset.rules && updatedOrNewRuleset.rules[ownProps.ruleIndex];

  return {
    ...ownProps,
    savedRule,
    isMandatory: isMandatory(
      campaign.type,
      (savedRuleset || updatedOrNewRuleset).subFeature,
      (savedRule || updatedOrNewRule).type,
    ),
  };
};

const actionCreators: IDispatchProps = {
  deleteRule: RulesetActions.deleteRule,
  editRule: RulesetActions.editRule,
};

export default connect(mapState, actionCreators)(Step);
